import axios from "axios";
import camelize from "camelize";
import router from "@/router";
import { getSavedState } from "@/helpers/LocalStorage.js";
const version = require("../../package.json").version;
const httpStore = axios.create({
  baseURL: process.env.VUE_APP_MAIN_ADMIN_API,
  headers: {
    Authorization: null
  },
  withCredentials: false,
  transformResponse: [
    function(data) {
      // Con responseType 'blob' (descarga de archivos), axios ya entrega
      // "data" como un Blob binario, no un string JSON — no intentar
      // parsearlo (this === config en esta función, axios la invoca con
      // fn.call(config, data, headers)) o la descarga queda vacía/corrupta.
      if (this?.responseType === "blob") return data;
      try {
        return JSON.parse(data);
      } catch (e) {
        return;
      }
    }
  ]
});

httpStore.interceptors.request.use(
  config => {
    const token = getCurrentToken();
    const selectedStoreURL = getSelectedStoreURL();
    if (token) {
      config.headers["Authorization"] = token;
      config.baseURL = selectedStoreURL ? selectedStoreURL : config.baseURL;
    } else {
      router.push({ name: "loginPage" });
      return false;
    }
    const selectedDepartmentCode = getSelectedDepartmentCode();
    if (selectedDepartmentCode) {
      config.headers["X-Department-Code"] = selectedDepartmentCode;
    }
    config.headers["X-App-Version"] = version;
    return config;
  },
  error => {
    Promise.reject(error);
  }
);

// Add a response interceptor
httpStore.interceptors.response.use(
  function(response) {
    // responseType 'blob' (descargas de archivos) nunca debe pasar por
    // camelize: response.data es un Blob binario, no JSON.
    if (!response.config.skipCamelize && response.config.responseType !== "blob") {
      response.data = camelize(response.data);
    }
    return response;
  },
  function(error) {
    // eslint-disable-next-line no-console
    if (error?.response?.status == 401) {
      window.localStorage.removeItem("auth.currentUser");
      router.push("login");
      return false;
    }
    if (
      error.response?.status === 426 &&
      error.response.data?.error === "VERSION_MISMATCH"
    ) {
      const data = error.response.data;

      // Generate unique tab ID for this session
      let tabId = sessionStorage.getItem("tab_id");
      if (!tabId) {
        tabId =
          Date.now() +
          "_" +
          Math.random()
            .toString(36)
            .substr(2, 9);
        sessionStorage.setItem("tab_id", tabId);
      }

      // Track refresh attempts per tab AND globally
      const globalRefreshAttempts = parseInt(
        localStorage.getItem("global_version_refresh_attempts") || "0"
      );
      const tabRefreshAttempts = parseInt(
        sessionStorage.getItem("tab_version_refresh_attempts") || "0"
      );
      const lastGlobalRefreshTime = parseInt(
        localStorage.getItem("last_global_refresh_time") || "0"
      );
      const currentTime = Date.now();

      // Reset global counter if last refresh was more than 2 minutes ago
      if (currentTime - lastGlobalRefreshTime > 2 * 60 * 1000) {
        localStorage.setItem("global_version_refresh_attempts", "0");
        // Also clear all tab-specific counters by broadcasting
        localStorage.setItem("clear_tab_counters", currentTime.toString());
      }

      // Listen for counter clear broadcast
      if (localStorage.getItem("clear_tab_counters")) {
        sessionStorage.setItem("tab_version_refresh_attempts", "0");
        localStorage.removeItem("clear_tab_counters");
      }

      // Check if we should block this tab from refreshing
      const shouldBlockRefresh =
        globalRefreshAttempts >= 3 || tabRefreshAttempts >= 2;

      if (shouldBlockRefresh) {
        console.error(
          `Tab ${tabId}: Refresh blocked. Global attempts: ${globalRefreshAttempts}, Tab attempts: ${tabRefreshAttempts}`
        );

        // If we've hit the absolute limit, offer to clear everything
        const isAbsoluteLimit =
          globalRefreshAttempts >= 5 || tabRefreshAttempts >= 3;

        let shouldRedirect;
        if (isAbsoluteLimit) {
          shouldRedirect = confirm(
            `Application update has failed multiple times. This may be due to corrupted data.\n\n` +
              `Click OK to clear all application data and restart, or Cancel to stay.\n\n` +
              `WARNING: This will log you out and clear all saved preferences.`
          );
        } else {
          shouldRedirect = confirm(
            `Application update failed. This appears to be a persistent issue.\n\n` +
              `Click OK to go to error page, or Cancel to stay on current page.\n\n` +
              `You may also try closing all tabs and reopening the application.`
          );
        }

        if (shouldRedirect) {
          if (isAbsoluteLimit) {
            // Nuclear option: clear everything and start fresh
            console.log("Nuclear option: Clearing all storage and restarting");

            // Clear all storage types
            const savedLanguage = localStorage.getItem("language");
            localStorage.clear();
            if (savedLanguage) localStorage.setItem("language", savedLanguage);
            sessionStorage.clear();

            // Clear IndexedDB if it exists
            if (window.indexedDB) {
              indexedDB
                .databases()
                .then(databases => {
                  databases.forEach(db => {
                    indexedDB.deleteDatabase(db.name);
                  });
                })
                .catch(() => {
                  // Ignore errors in IndexedDB cleanup
                });
            }

            // Clear service workers
            if ("serviceWorker" in navigator) {
              navigator.serviceWorker.getRegistrations().then(registrations => {
                registrations.forEach(registration =>
                  registration.unregister()
                );
              });
            }

            // Clear cookies (if your app uses them)
            document.cookie.split(";").forEach(cookie => {
              const eqPos = cookie.indexOf("=");
              const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
              document.cookie =
                name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
            });

            // Add a flag to prevent infinite loops after clearing
            sessionStorage.setItem("storage_cleared", "true");

            // Redirect to login with a clean slate
            window.location.href = "/login?cleared=true";
          } else {
            // Regular cleanup
            const savedLanguage = localStorage.getItem("language");
            localStorage.clear();
            if (savedLanguage) localStorage.setItem("language", savedLanguage);
            sessionStorage.clear();
            window.location.href = "/login?cleared=true";
          }
        }

        return Promise.reject(error);
      }

      // Check if another tab is already refreshing
      const refreshingTab = localStorage.getItem("refreshing_tab_id");
      const refreshStartTime = parseInt(
        localStorage.getItem("refresh_start_time") || "0"
      );

      // If another tab started refreshing less than 10 seconds ago, wait
      if (
        refreshingTab &&
        refreshingTab !== tabId &&
        currentTime - refreshStartTime < 10000
      ) {
        console.log(
          `Tab ${tabId}: Another tab (${refreshingTab}) is already refreshing, waiting...`
        );

        // Wait for the other tab to finish refreshing
        setTimeout(() => {
          window.location.reload();
        }, 2000 + Math.random() * 3000); // Random delay to prevent thundering herd

        return Promise.reject(error);
      }

      // This tab will handle the refresh
      localStorage.setItem("refreshing_tab_id", tabId);
      localStorage.setItem("refresh_start_time", currentTime.toString());

      // Increment counters
      localStorage.setItem(
        "global_version_refresh_attempts",
        (globalRefreshAttempts + 1).toString()
      );
      localStorage.setItem("last_global_refresh_time", currentTime.toString());
      sessionStorage.setItem(
        "tab_version_refresh_attempts",
        (tabRefreshAttempts + 1).toString()
      );

      console.log(
        `Tab ${tabId}: Handling version mismatch. Global attempts: ${globalRefreshAttempts +
          1}, Tab attempts: ${tabRefreshAttempts + 1}`
      );

      if (data.actions?.clearStorage) {
        // Don't clear our tracking data
        const backupData = {
          globalRefreshAttempts: localStorage.getItem(
            "global_version_refresh_attempts"
          ),
          lastGlobalRefreshTime: localStorage.getItem(
            "last_global_refresh_time"
          ),
          refreshingTabId: localStorage.getItem("refreshing_tab_id"),
          refreshStartTime: localStorage.getItem("refresh_start_time"),
          language: localStorage.getItem("language"),
          tabRefreshAttempts: sessionStorage.getItem(
            "tab_version_refresh_attempts"
          ),
          tabId: sessionStorage.getItem("tab_id")
        };

        localStorage.clear();
        sessionStorage.clear();

        // Restore tracking data
        Object.entries(backupData).forEach(([key, value]) => {
          if (value !== null) {
            if (key.startsWith("tab") || key === "tabId") {
              sessionStorage.setItem(key.replace("tab", "tab_"), value);
            } else {
              localStorage.setItem(
                key.replace(/([A-Z])/g, "_$1").toLowerCase(),
                value
              );
            }
          }
        });
      }

      if (data.actions?.clearServiceWorker) {
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(registration => registration.unregister());
          });
        }
      }

      if (data.actions?.forceRefresh) {
        console.log(
          `Tab ${tabId}: Force refreshing...`,
          data.actions.refreshMessage || data.message
        );

        // Notify other tabs that refresh is starting
        localStorage.setItem("refresh_in_progress", currentTime.toString());

        // Force fresh reload with cache-busting
        const url = new URL(window.location);
        url.searchParams.set("v", currentTime);
        url.searchParams.set("cache_bust", tabId);
        window.location.replace(url.toString());
      }
    }
    return Promise.reject(error);
  }
);

// Listen for storage events to coordinate between tabs
window.addEventListener("storage", e => {
  if (e.key === "refresh_in_progress") {
    // Another tab is refreshing, this tab should also refresh after a delay
    setTimeout(() => {
      window.location.reload();
    }, 1000 + Math.random() * 2000);
  }

  if (e.key === "clear_tab_counters") {
    sessionStorage.setItem("tab_version_refresh_attempts", "0");
  }
});

function getCurrentToken() {
  try {
    return getSavedState("auth.currentUser")?.token || null;
  } catch (e) {
    return null;
  }
}

export function getSelectedStoreURL() {
  try {
    return getSavedState("store.selectedStoreUrl") || null;
  } catch (e) {
    return null;
  }
}

export function getSelectedDepartmentCode() {
  try {
    const code = getSavedState("store.selectedDepartment");
    return code !== "ALL" ? code : null;
  } catch (e) {
    return null;
  }
}

export default httpStore;
